import { Search } from 'lucide-react';
import React, { useState, useEffect } from 'react';

const GalleryDisplay = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isViewingImages, setIsViewingImages] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://atabackend.quindl.com/gallery/get');
      if (!response.ok) throw new Error('Failed to fetch events');
      const data = await response.json();
      setEvents(data);
      console.log(data);
      if (data.length > 0) {
        const years = [...new Set(data.map(event => 
          new Date(event.date).getFullYear()
        ))];
        setSelectedYear(Math.min(...years));
      }
    } catch (error) {
      setError('Failed to load events');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsViewingImages(true);
  };

  const handleBackClick = () => {
    setSelectedEvent(null);
    setIsViewingImages(false);
  };

  const eventsByYear = events.reduce((acc, event) => {
    const year = new Date(event.date).getFullYear();
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(event);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-500">{error}</div>
      </div>
    );
  }

  if (isViewingImages && selectedEvent) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          <button
            onClick={handleBackClick}
            className="inline-flex items-center px-4 py-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
          >
            <span className="mr-2 text-xl">←</span>
            <span className="text-lg">Back to Gallery</span>
          </button>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {selectedEvent.name}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {selectedEvent.images?.map((image, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl"
              >
                <div className="aspect-square relative">
                  <img
                    src={`${process.env.REACT_APP_BASE_URL}/uploads/${image}`}
                    alt={`${selectedEvent.name} - Image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const years = Object.keys(eventsByYear).sort((a, b) => b - a); // Reverse chronological order

  return (
    <div className="min-h-screen bg-gray-50 mt-6">
      <div className="max-w-7xl mx-auto p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-12">Gallery</h1>
        
        {
                  years.length === 0 && (
                    <div className="text-center py-16 bg-gray-50 rounded-xl">
              <Search className="mx-auto w-16 h-16 text-gray-300 mb-4" />
              <p className="text-xl text-gray-500">No Gallery found</p>
            </div>
                  )
                }
        <div className="flex flex-row-reverse gap-12">
          {/* Timeline - Adjusted dot sizes */}
          
          <div className="w-24 flex-shrink-0">
            <div className="sticky top-8">
              <div className="relative flex items-start h-full">
                <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gray-300" />
                
                <div className="relative flex flex-col items-start space-y-12">
                  {years.map((year) => (
                    <button
                      key={year}
                      onClick={() => setSelectedYear(parseInt(year))}
                      className="group relative flex items-center"
                    >
                      <div
                        className={`w-4 h-4 rounded-full z-10 transition-all duration-200
                          ${selectedYear === parseInt(year)
                            ? 'bg-orange-500 ring-2 ring-orange-200'
                            : 'bg-gray-300 group-hover:bg-gray-400'}`}
                      />
                      <span
                        className={`ml-4 text-lg transition-colors duration-200
                          ${selectedYear === parseInt(year)
                            ? 'text-orange-500 font-bold'
                            : 'text-gray-600 group-hover:text-gray-900'}`}
                      >
                        {year}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Gallery Grid */}
          {
            eventsByYear[selectedYear] && eventsByYear[selectedYear].length === 0 && (
              <div className="flex justify-center items-center h-full">
                <div className="text-xl text-gray-600">No events found for {selectedYear}</div>
              </div>
              )
          }
          <div className="flex-grow">
            {selectedYear && eventsByYear[selectedYear] && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {eventsByYear[selectedYear].map((event) => (
                  <div
                    key={event._id}
                    onClick={() => handleEventClick(event)}
                    className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                  >
                    {event.images?.length > 0 && (
                      <div className="relative aspect-video bg-gray-100">
                        <img
                          src={`${process.env.REACT_APP_BASE_URL}/uploads/${event.images[0]}`}
                          alt={event.name}
                          className="w-full h-full object-cover"
                        />
                        {event.images.length > 1 && (
                          <div className="absolute bottom-3 right-3 bg-black bg-opacity-75 text-white px-3 py-1 rounded-full text-sm font-medium">
                            +{event.images.length - 1}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="p-6">
                      <h2 className="text-xl font-semibold text-gray-900 mb-2 truncate">
                        {event.name}
                      </h2>
                      <p className="text-gray-600">
                        {new Date(event.date).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryDisplay;